// @flow
import React, { type Node } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withFormsy } from "formsy-react";
import FormGroup from "react-bootstrap/lib/FormGroup";
import AttributeLabel from "components/ExtraAttributes/AttributeLabel.js";
import Error from "components/ExtraAttributes/Error.js";
import styles from "components/ExtraAttributes/AttributeLabel.module.css";

export type FormsyEditorBaseDTO = {
    name: string,
    value: any,
};

type Props = {
    /** class attribute for wrapper */
    baseClassName: string,
    /** label to display */
    header: string,
    /** unique field name */
    name: string,
    /** current field's value */
    value: string,
    /** React component to render after header before value */
    afterHeaderComponentClass?: Node,
    /** React component to render value */
    render: (value: any, changeValue: Function) => Node,
    /** Additional react component to render */
    additionalRender?: Node,
    /** Should Formsy Base render just the child? (No wrapping container) */
    isAdditionalRender: boolean,
    /** callback when value has been changed
     * @param {string} name field's name
     * @param {any} value new field's value
     */
    /** whether to render validation below */
    showValidationBelow: boolean,
    /** whether form field is required */
    isRequired: Boolean,
    onChange?: (name: string, value: any) => void,
    /** whether to show field's label */
    showLabel: boolean,
    /** withFormsy(): setValue */
    setValue: Function,
    /** withFormsy(): isValid */
    isValid: boolean,
    /** withFormsy(): errorMessage */
    errorMessage: ?string,
    /** callback when an attribute is edited
     * @param {string} name unique name of attribute being changed
     * @param {any} value new value
     */
    onChange: (name: string, value: any) => void,
    /** optional key to force refresh */
    loadIndex?: any,
    /** optional extras */
    extras?: any,
};

type State = {
    /** whether form field is focused */
    focused: boolean,
};

export class FormsyEditorBase extends React.Component<Props, State> {
    static defaultProps = {
        showLabel: true,
        isAdditionalRender: false,
        additionalRender: null,
        showValidationBelow: false,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            focused: false,
        };
    }

    _changeValue = (value: any) => {
        this.props.setValue(value);
        /* istanbul ignore else */
        if (typeof this.props.onChange === "function") {
            this.props.onChange(this.props.name, value);
        }
    };

    changeValue = (
        changed: SyntheticInputEvent<*> | Array<FormsyEditorBaseDTO>
    ) => {
        if (
            changed &&
            Object.prototype.hasOwnProperty.call(changed, "currentTarget")
        ) {
            this._changeValue(changed.currentTarget.value);
        } else if (changed && Array.isArray(changed) && changed.length > 0) {
            changed.forEach(({ name, value }) => {
                if (this.props.name === name) {
                    this._changeValue(value, true); // HACK: if array of objects, assume lookup and force re-render #51586
                } else if (typeof this.props.onChange === "function") {
                    this.props.onChange(name, value);
                } else {
                    console.warn(`Cannot update ${this.props.name}`);
                }
            });
        } else {
            console.warn(
                `Unknown changed type, cannot update ${this.props.name}`
            );
        }
    };

    /* istanbul ignore next */ _onFocus = () =>
        this.setState({ focused: true });

    /* istanbul ignore next */ _onBlur = () => {
        this.setState({ focused: true });
    };

    _renderField = () => (
        <>
            {this.props.render(
                this.props.value,
                this.changeValue,
                this.props.isValid
            )}
            {!this.props.isValid && (
                <Error
                    errorMessage={this.props.errorMessage}
                    showValidationBelow={this.props.showValidationBelow}
                />
            )}
        </>
    );

    render() {
        if (this.props.isAdditionalRender) {
            return this._renderField();
        }
        // console.log(this.props.name, this.props);
        // console.log(`render ${this.props.name}`, this.props.getValue());
        return (
            <FormGroup
                className={this.props.baseClassName}
                controlId={this.props.name}
                validationState={this.props.isValid ? null : "error"}
                onFocus={this._onFocus}
                onBlur={this._onBlur}
                data-test={`formGroup${this.props.name?.replace(" ", "")}${
                    this.props.isRequired /* istanbul ignore next */ &&
                    "Required"
                }`}
            >
                {this.props.showLabel && (
                    <AttributeLabel
                        className={styles.label}
                        label={this.props.header}
                    />
                )}
                {typeof this.props.afterHeaderComponentClass === "function" &&
                    this.props.afterHeaderComponentClass()}
                <div className="form-field">
                    {this._renderField()}
                    {this.props.additionalRender}
                </div>
            </FormGroup>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    showValidationBelow: !state.browser.greaterThanOrEqual.medium,
    ...ownProps,
});

const FormsyEditorBaseContainer = compose(
    withFormsy,
    connect(mapStateToProps, null, null, { forwardRef: true })
)(FormsyEditorBase);

export default FormsyEditorBaseContainer;
